import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";

import {
  IoDocumentOutline,
  IoSettingsOutline,
  IoEllipsisHorizontal,
  IoCloseCircleOutline,
  IoGridOutline,
} from "react-icons/io5";

import TableSize from "../../components/common/TableSize";
import Dropdown from "../../components/common/Dropdown";
import Toaster from "../../components/common/Toaster";
import Modal from "../../components/common/Modal";

import CorForm from "./components/CorForm";
import CorTable from "./components/CorTable";
import CorBreadcrumb from "./components/CorBreadcrumb";
import CorHeader from "./components/CorHeader";
import CorForms from "./components/CorForms";
import CorCancelForm from "./components/CorCancelForm";
import CorFilterForm from "./components/CorFilterForm";

import accountService from "../../services/accountService";

const AccountCors = () => {
  const { id } = useParams();

  const [data, setData] = useState([]);
  const [account, setAccount] = useState(null);
  const [showFilter, setShowFilter] = useState(false);

  const [info, setInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showItems, setShowItems] = useState(false);

  const [tableOptions, setTableOptions] = useState({
    status: "",
    start_date: "",
    end_date: "",
    search_query: "",
    current_page: 1,
    page_size: 10,
    sort_column: { path: "date_generated", order: "desc" },
  });

  const actions = (data) => {
    return [
      [
        {
          name: "View Document",
          icon: <IoDocumentOutline className="mr-3 text-green-500" />,
          is_disabled: !data.s3_reference_name,
          action: () =>
            window.open(
              data.s3_reference_name,
              "_blank",
              "noopener, noreferrer"
            ),
        },
        {
          name: "View Tax Types",
          icon: <IoGridOutline className="mr-3 text-blue-400" />,
          action: () => handleItems(data),
        },
      ],
      [
        {
          name: "Edit Info",
          icon: <IoSettingsOutline className="mr-3 text-gray-400" />,
          action: () => handleEdit(data),
        },
      ],
      [
        {
          name: "Cancel COR",
          icon: <IoCloseCircleOutline className="mr-3 text-red-500" />,
          action: () => handleCancel(data),
        },
      ],
    ];
  };

  const columns = [
    { path: "ocn", label: "OCN" },
    { path: "rdo", label: "RDO" },
    { path: "status_type", label: "Status" },
    { path: "date_generated", label: "Date Generated" },
    {
      key: "actions",
      content: (data) => (
        <Dropdown items={actions(data)} icon={<IoEllipsisHorizontal />} />
      ),
    },
  ];

  const { page_size: size } = tableOptions;

  const settings = [
    {
      id: 1,
      content: <TableSize size={size} onChange={(e) => handleSizeChange(e)} />,
    },
  ];

  const fetchAccount = useCallback(
    async (mounted) => {
      try {
        const result = await accountService.getAccountInfoAsync(id);
        if (result === null) return window.location("/404");

        if (mounted) {
          setAccount(result);
        }
      } catch (ex) {
        Toaster.Error("Something went wrong.");
      }
    },
    [id]
  );

  useEffect(() => {
    let mounted = true;

    fetchAccount(mounted);

    return () => {
      mounted = false;
    };
  }, [fetchAccount]);

  const handleOptionsChange = (options) => setTableOptions(options);

  const handleSizeChange = (event) => {
    const page_size = event.target.value;

    if (page_size !== tableOptions.page_size) {
      const options = { ...tableOptions };
      options.page_size = page_size;
      options.current_page = 1;
      setTableOptions(options);
    }
  };

  const handleSearch = (query) => {
    const options = { ...tableOptions };

    if (options.search_query !== query) {
      options.selected_type = "";
      options.start_date = "";
      options.end_date = "";
      options.search_query = query;
      options.current_page = 1;
      handleOptionsChange(options);
    }
  };

  const handleFilter = (status, start_date, end_date) => {
    const options = { ...tableOptions };
    options.status = status;
    options.current_page = 1;
    options.start_date = start_date;
    options.end_date = end_date;
    handleOptionsChange(options);
    setShowFilter(false);
  };

  const handleEdit = (item) => {
    const result = item ?? null;
    setInfo(result);
    setShowForm(!showForm);
  };

  const handleCancel = (item) => {
    const result = item ?? null;
    setInfo(result);
    setShowCancel(!showCancel);
  };

  const handleItems = (item) => {
    const result = item ?? null;
    setInfo(result);
    setShowItems(!showItems);
  };

  const handleSuccess = (info) => {
    const index = data.findIndex((i) => i.id === info.id);

    if (index === -1) {
      setData([info, ...data]);
    } else {
      const items = [...data];
      items[index] = info;

      setData(items);
    }
  };

  const handleCancellationSuccess = (info) => {
    const index = data.findIndex((i) => i.id === info.id);
    const items = [...data];
    items[index] = info;

    setData(items);
    handleCancel();
  };

  return (
    <div className="px-6 lg:px-0">
      <CorBreadcrumb account={account} />
      <div className="grid grid-cols-2 gap-5">
        <div className="col-span-2">
          <div className="card p-8 mt-5">
            <CorHeader
              id={id}
              account={account}
              settings={settings}
              onFilter={setShowFilter}
              onSearch={handleSearch}
              onSuccess={handleSuccess}
            />
            <div className="mt-10">
              <CorTable
                id={id}
                data={data}
                columns={columns}
                tableOptions={tableOptions}
                setData={setData}
                onChange={handleOptionsChange}
              />
              <Modal
                title="Filter"
                isOpen={showFilter}
                onClose={() => setShowFilter(false)}
                borderTop
              >
                <CorFilterForm onFilter={handleFilter} />
              </Modal>
              <Modal
                size="lg"
                title="Update COR"
                isOpen={showForm}
                isLoading={isLoading}
                onClose={() => setShowForm(false)}
                borderTop
              >
                <CorForm
                  id={id}
                  data={info}
                  isLoading={isLoading}
                  setLoading={setIsLoading}
                  setShow={setShowForm}
                  onSuccess={handleSuccess}
                />
              </Modal>
              <Modal
                title="Cancel COR"
                isOpen={showCancel}
                onClose={() => handleCancel()}
                borderTop
              >
                <CorCancelForm
                  data={info}
                  onSuccess={handleCancellationSuccess}
                />
              </Modal>
              <Modal
                size="lg"
                title="COR - Tax Types"
                isOpen={showItems}
                onClose={() => handleItems()}
                borderTop
              >
                <CorForms data={info} />
              </Modal>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountCors;
